import {QueryKey, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import api from "@/lib/api";
import {normalizePage} from "@/lib/paging";

// ───────────────────────────────────────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────────────────────────────────────
export type TeamMemberId = string;

export type TeamMember = {
    id: TeamMemberId;
    name: string;
    description: string;
    imageUrl: string;
};

export type SaveTeamMemberInput = {
    id?: TeamMemberId;
    name: string;
    description: string;
    file?: File;
    onUploadProgress?: (pct: number) => void;
};

export type SortField = "name";
export type SortOrder = "asc" | "desc";

export type HookParams = {
    page?: number;
    size?: number;
    sortBy?: SortField;
    sortOrder?: SortOrder;
};

export type TeamMemberPage = ReturnType<typeof normalizePage<TeamMember>>;

// ───────────────────────────────────────────────────────────────────────────────
// Constants
// ───────────────────────────────────────────────────────────────────────────────
const PUBLIC_BASE = "/api/v1/public/team-members";
const ADMIN_BASE = "/api/v1/editor/team-members";

const DEFAULTS = {
    page: 0,
    size: 10,
    sortBy: "name" as SortField,
    sortOrder: "asc" as SortOrder,
};

// ───────────────────────────────────────────────────────────────────────────────
// Query Keys
// ───────────────────────────────────────────────────────────────────────────────
export const teamMemberKeys = {
    all: ["team-members"] as const,
    list: (params?: HookParams) => [...teamMemberKeys.all, "list", params ?? {}] as const,
    byId: (id: TeamMemberId) => [...teamMemberKeys.all, "byId", id] as const,
};

// ───────────────────────────────────────────────────────────────────────────────
// Fetchers
// ───────────────────────────────────────────────────────────────────────────────
function emptyPage(page = DEFAULTS.page, size = DEFAULTS.size): TeamMemberPage {
    return normalizePage<TeamMember>({page, size, total: 0, content: []});
}

async function fetchTeamMembers(params: HookParams = {}): Promise<TeamMemberPage> {
    const page = params.page ?? DEFAULTS.page;
    const size = params.size ?? DEFAULTS.size;
    const sortBy = params.sortBy ?? DEFAULTS.sortBy;
    const sortOrder = params.sortOrder ?? DEFAULTS.sortOrder;

    const sort = `${sortBy},${sortOrder}`;
    const {data} = await api.get(PUBLIC_BASE, {params: {page, size, sort}});

    if (Array.isArray(data?.content)) return normalizePage<TeamMember>(data);
    if (Array.isArray(data?.items) && typeof data?.total === "number") return data;
    return emptyPage(page, size);
}

async function fetchTeamMemberById(id: TeamMemberId): Promise<TeamMember> {
    const {data} = await api.get(`${PUBLIC_BASE}/${encodeURIComponent(id)}`);
    return data;
}

// ───────────────────────────────────────────────────────────────────────────────
// Queries
// ───────────────────────────────────────────────────────────────────────────────
export function useTeamMembers(params?: HookParams) {
    const q: HookParams = {
        page: params?.page ?? DEFAULTS.page,
        size: params?.size ?? DEFAULTS.size,
        sortBy: params?.sortBy ?? DEFAULTS.sortBy,
        sortOrder: params?.sortOrder ?? DEFAULTS.sortOrder,
    };

    return useQuery({
        queryKey: teamMemberKeys.list(q),
        queryFn: () => fetchTeamMembers(q),
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    });
}

export function useTeamMember(id?: TeamMemberId) {
    return useQuery({
        queryKey: id ? teamMemberKeys.byId(id) : ["team-members", "byId", "empty"],
        queryFn: () => fetchTeamMemberById(id!),
        enabled: Boolean(id),
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    });
}

// ───────────────────────────────────────────────────────────────────────────────
// Mutations
// ───────────────────────────────────────────────────────────────────────────────
export function useSaveTeamMember() {
    const qc = useQueryClient();

    return useMutation({
        mutationKey: [...teamMemberKeys.all, "save"],
        mutationFn: async (payload: SaveTeamMemberInput): Promise<TeamMember> => {
            const form = new FormData();
            form.append("name", payload.name.trim());
            form.append("description", payload.description.trim());
            if (payload.file) form.append("file", payload.file);

            const cfg: any = {headers: {"Content-Type": "multipart/form-data"}};
            if (payload.onUploadProgress) {
                cfg.onUploadProgress = (evt: ProgressEvent) => {
                    if (!evt.total) return;
                    payload.onUploadProgress!(Math.round((evt.loaded * 100) / evt.total));
                };
            }

            if (payload.id) {
                const {data} = await api.put(`${ADMIN_BASE}/${payload.id}`, form, cfg);
                return data;
            } else {
                const {data} = await api.post(ADMIN_BASE, form, cfg);
                return data;
            }
        },
        onSuccess: () => {
            qc.invalidateQueries({queryKey: teamMemberKeys.all});
        },
    });
}

export function useDeleteTeamMember() {
    const qc = useQueryClient();

    return useMutation({
        mutationKey: [...teamMemberKeys.all, "delete"],
        mutationFn: async (id: TeamMemberId) => {
            await api.delete(`${ADMIN_BASE}/${id}`);
            return id;
        },
        onMutate: async (id) => {
            await qc.cancelQueries({queryKey: teamMemberKeys.all});

            const snapshots = qc.getQueriesData<unknown>({queryKey: teamMemberKeys.all});

            snapshots.forEach(([key, value]) => {
                const page = value as Partial<TeamMemberPage> | undefined;
                if (page && Array.isArray(page.items)) {
                    const next: TeamMemberPage = {
                        ...(page as TeamMemberPage),
                        items: page.items.filter((m: any) => m.id !== id),
                        total: Math.max(0, (page.total as number) - 1),
                    };
                    qc.setQueryData(key, next);
                }
            });

            return {snapshots};
        },
        onError: (_err, _id, ctx) => {
            ctx?.snapshots?.forEach(([key, value]: [unknown, unknown]) => {
                qc.setQueryData(<QueryKey>key, value);
            });
        },
        onSettled: () => {
            qc.invalidateQueries({queryKey: teamMemberKeys.all});
        },
    });
}
